import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { callFirebaseFunction } from '../../../../utils/helpers';

import './styles.css';

function SideBarDirectory({ directory, onProjectClick, organizationId, searchQuery = '', selectedProjectId }) {
	const [showChildren, setShowChildren] = useState(false);
	const [projects, setProjects] = useState([]);
	const [loading, setLoading] = useState(false);
	const [fetched, setFetched] = useState(false);

	const fetchProjects = async () => {
		setLoading(true);
		try {
			// Use new Cloud Function instead of direct Firestore read
			const projectsData = await callFirebaseFunction('getProjects', {
				directoryId: directory.id,
				organizationId: organizationId
			});
			
			const projects = projectsData.map((proj) => ({
				id: proj.id,
				name: proj.name,
				creationDate: proj.creationDate,
				images: proj.images,
				csvURL: proj.csvURL,
				directoryId: proj.directoryId,
				organizationId: proj.organizationId,
			}));

			setProjects(projects);
			setFetched(true);
		} catch (error) {
			console.error('Error fetching projects:', error);
		} finally {
			setLoading(false);
		}
	};

	const onDirectoryClick = () => {
		setShowChildren(!showChildren);
		if (!fetched) fetchProjects();
	};

	const search = searchQuery.toLowerCase().trim();
	const sortedProjects = projects.sort((a, b) => a.name.localeCompare(b.name));

	const dirNameMatches = !search || directory.name.toLowerCase().includes(search);
	const isExpanded = search ? dirNameMatches && showChildren : showChildren;

	if (!dirNameMatches) {
		return null;
	}

	const formatDate = (creationDate) => {
		if (!creationDate) return null;
		
		let date;
		
		// Handle Firestore Timestamp with toDate() method
		if (creationDate.toDate && typeof creationDate.toDate === 'function') {
			date = creationDate.toDate();
		}
		// Handle Firestore Timestamp serialized format {_seconds, _nanoseconds}
		else if (creationDate._seconds) {
			date = new Date(creationDate._seconds * 1000);
		}
		// Handle ISO string or timestamp
		else if (typeof creationDate === 'string' || typeof creationDate === 'number') {
			date = new Date(creationDate);
		}
		// Handle Date object
		else if (creationDate instanceof Date) {
			date = creationDate;
		}
		else {
			return null;
		}
		
		// Check if date is valid
		if (isNaN(date.getTime())) {
			return null;
		}
		
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	};

	const FolderIcon = isExpanded ? FolderOpenOutlinedIcon : FolderOutlinedIcon;

	return (
		<div className="dir">
			<button className={`dir__folder ${isExpanded ? 'dir__folder--expanded' : ''}`} onClick={onDirectoryClick}>
				<ChevronRightIcon
					className={`dir__chevron ${isExpanded ? 'dir__chevron--open' : ''}`}
					sx={{ fontSize: 16 }}
				/>
				<FolderIcon
					className="dir__folder-icon"
					sx={{ fontSize: 16, color: isExpanded ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.42)' }}
				/>
				<span className="dir__folder-name">{directory.name}</span>
				{fetched && !search && (
					<span className="dir__folder-count">{sortedProjects.length}</span>
				)}
			</button>
			{isExpanded && (
				<div className="dir__children">
					{loading && sortedProjects.length === 0 ? (
						<div className="dir__loading">
							<CircularProgress size={12} sx={{ color: 'rgba(255,255,255,0.25)' }} />
						</div>
					) : (
						sortedProjects.map((project) => {
							const imageCount = project.images?.length || 0;
							const date = formatDate(project.creationDate);
							const isSelected = project.id === selectedProjectId;
							return (
								<button
									key={project.id}
									className={`dir__project ${isSelected ? 'dir__project--selected' : ''}`}
									onClick={() => onProjectClick(project, directory.id)}
								>
									<div className="dir__project-title">{project.name}</div>
									<div className="dir__project-meta">
										{date && <span>{date}</span>}
										{date && <span className="dir__meta-dot">&middot;</span>}
										<span>{imageCount} {imageCount === 1 ? 'photo' : 'photos'}</span>
									</div>
								</button>
							);
						})
					)}
				</div>
			)}
		</div>
	);
}

export default SideBarDirectory;
